import { Heading, Stack, Text, textVariants, vars } from '@etg/wings';
import { css, cx, resetButtonStyle } from '@eti/styles';
import type { ReactNode } from 'react';
import { LoadingDots } from '@eti/components';
import { useSnowplowTracking } from '@eti/providers';
import { focusStyles } from '../../../../utils/styleUtils';

const buttonStyles = (isSelected: boolean, isDisabled: boolean) => css`
  ${resetButtonStyle}; /* stylelint-disable-next-line */
  background-color: ${isSelected && vars.colors.inputs.hover};
  border: ${isSelected
    ? `2px solid ${vars.colors.inputs.main}`
    : `1px solid ${vars.colors.inputs.border}`};
  border-radius: ${vars.button.borderRadius};
  cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
  display: flex;
  flex: 1;
  justify-content: center;
  margin: ${isSelected ? '0' : '1px'};
  padding: 16px;

  :hover {
    background-color: ${!isDisabled && vars.colors.inputs.hover};
    cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
  }
`;

interface ManageTripOptionButtonProps {
  className?: string;
  icon: ReactNode;
  dataTestId: string;
  title: string;
  subtitle: string;
  onClick: () => void;
  isSelected: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  snowplowLabel?: string;
}

const ManageTripOptionButton = ({
  className,
  icon,
  dataTestId,
  title,
  subtitle,
  onClick,
  isSelected,
  isDisabled = false,
  isLoading = false,
  snowplowLabel,
}: ManageTripOptionButtonProps) => {
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();

  const handleOnClick = () => {
    if (snowplowLabel && isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Manage Trip',
        action: 'Click',
        label: snowplowLabel,
      });
    }

    onClick();
  };

  return (
    <button
      aria-controls={`tab-panel-${dataTestId}`}
      aria-selected={isSelected}
      className={cx(buttonStyles(isSelected, isDisabled), focusStyles, className)}
      data-testid={dataTestId}
      disabled={isDisabled}
      id={dataTestId}
      onClick={handleOnClick}
      role="tab"
      type="button"
    >
      <Stack align="center" spacing={8}>
        {icon}
        <Stack align="center" spacing={4}>
          <Heading as="div" level={3}>
            {title}
          </Heading>
          {isLoading ? <LoadingDots /> : <Text variant={textVariants._12}>{subtitle}</Text>}
        </Stack>
      </Stack>
    </button>
  );
};

export default ManageTripOptionButton;
